import { Play } from '@tunasong/icons'
import type { GroupingMeterKind, GroupingQuestion } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { RhythmAnswerButton } from './answer-button.js'
import type { RhythmAnswerButtonState } from './answer-button.js'
import styles from './grouping-question.module.css'

export interface GroupingQuestionPanelProps {
  question: GroupingQuestion
  scoreCorrect: number
  scoreAnswered: number
  interactive: boolean
  onHearIt: () => void
  onAnswer: (pickedGroupingId: string) => void
}

const meterLabel = (meter: GroupingMeterKind): string => (meter === '5-4' ? '5/4' : '7/8')

/**
 * Rhythm Trainer Grouping ID question panel. The meter is named in the prompt
 * (it is given, not the answer); the answer grid is the drawn meter's grouping
 * options — 2 for 5/4, 3 for 7/8. Stateless — the host owns session state.
 *
 * Spec: docs/superpowers/specs/2026-06-01-rhythm-trainer-v1.5-design.md §5
 */
export const GroupingQuestionPanel: FC<GroupingQuestionPanelProps> = props => {
  const { question, scoreCorrect, scoreAnswered, interactive, onHearIt, onAnswer } = props
  const buttonState: RhythmAnswerButtonState = interactive ? 'idle' : 'dimmed'
  return (
    <section className={styles.question} aria-label="Rhythm Trainer grouping question">
      <header className={styles.header}>
        <span
          className={styles.scoreChip}
          aria-label={`${scoreCorrect} correct out of ${scoreAnswered} answered`}
        >
          {scoreCorrect} / {scoreAnswered}
        </span>
      </header>
      <p className={styles.prompt}>This is {meterLabel(question.entry.meter)} — which grouping?</p>
      <div className={styles.actionRow}>
        <Button variant="outlined" size="small" startIcon={<Play />} onClick={onHearIt}>
          Hear it
        </Button>
      </div>
      <div className={styles.cardGrid}>
        {question.options.map(o => (
          <RhythmAnswerButton
            key={o.groupingId}
            pickValue={o.groupingId}
            lexiconId={o.lexiconId}
            fallbackLongName={o.label}
            fallbackShortName={o.label}
            state={buttonState}
            onPick={onAnswer}
          />
        ))}
      </div>
    </section>
  )
}
